import React, { useCallback, useEffect, useRef, useState } from "react";
import { logError } from "../lib/log";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MessageCircle, ShieldBan } from "lucide-react";
import { ChatMessage } from "../components/ChatMessage";
import { ChatComposer } from "../components/ChatComposer";

function useClassChat(user) {
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [muted, setMuted] = useState([]);
  const isTeacher = user?.role === "teacher";

  const load = useCallback(() => api.get("/class/messages").then((r) => setMsgs(r.data.messages)).catch((e) => setErr(apiError(e, "Load failed"))), []);
  const loadMutes = useCallback(() => api.get("/class/mutes").then((r) => setMuted(r.data.mutes || [])).catch((e) => logError("Could not load mutes", e)), []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    if (isTeacher) loadMutes();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [user, isTeacher, load, loadMutes]);

  const run = async (fn, fallback) => {
    setErr(null);
    try { await fn(); } catch (e) { setErr(apiError(e, fallback)); }
  };
  const send = async (text) => {
    setBusy(true);
    await run(async () => { await api.post("/class/messages", { content: text }); await load(); }, "Send failed");
    setBusy(false);
  };
  const deleteMsg = (mid) => window.confirm("Delete this message?") && run(async () => { await api.delete(`/class/messages/${mid}`); await load(); }, "Delete failed");
  const mute = (uid, minutes) => run(async () => { await api.post("/class/mute", { user_id: uid, duration_minutes: minutes }); await loadMutes(); }, "Mute failed");
  const unmute = (uid) => run(async () => { await api.post("/class/unmute", { user_id: uid }); await loadMutes(); }, "Unmute failed");
  return { msgs, busy, err, muted, send, deleteMsg, mute, unmute };
}

function ChatHeader({ isTeacher }) {
  return (
    <div>
      <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs uppercase tracking-widest text-cyan-300"><MessageCircle size={12} /> Class chat · ChemBuddy is here</div>
      <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Class chatroom</h1>
      <p className="mt-2 text-sm text-slate-400">Ask anything. End with a <code className="rounded bg-white/10 px-1 font-mono-num">?</code> or mention <code className="rounded bg-white/10 px-1 font-mono-num">@bot</code> to get an instant answer from ChemBuddy.</p>
      {isTeacher && <Link to="/teacher" data-testid="chat-filter-link" className="mt-2 inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200"><ShieldBan size={12} /> Edit blocked words for this class →</Link>}
    </div>
  );
}

export default function ClassChat() {
  const { user, loading } = useAuth();
  const { msgs, busy, err, muted, send, deleteMsg, mute, unmute } = useClassChat(user);
  const bottomRef = useRef(null);
  const isTeacher = user?.role === "teacher";
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  if (loading) return <div className="text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="space-y-6" data-testid="class-chat-root">
      <ChatHeader isTeacher={isTeacher} />

      {err && <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200" data-testid="chat-error">{err}</div>}

      <div className="flex h-[55dvh] min-h-[320px] flex-col rounded-md sm:h-[60vh] border border-white/10 bg-black/40">
        <div className="flex-1 space-y-3 overflow-y-auto p-3 sm:p-5" data-testid="chat-messages">
          {msgs.length === 0 && !err && <div className="text-center text-sm text-slate-500">No messages yet. Start the conversation!</div>}
          {msgs.map((m) => (
            <ChatMessage key={m.id} m={m} me={user} muteRec={muted.find((x) => x.user_id === m.user_id)} onDelete={deleteMsg} onMute={mute} onUnmute={unmute} />
          ))}
          <div ref={bottomRef} />
        </div>
        <ChatComposer onSend={send} busy={busy} />
      </div>

      <div className="text-center">
        <Link to={isTeacher ? "/teacher" : "/dashboard"} className="text-xs text-slate-400 hover:text-cyan-300">← Back</Link>
      </div>
    </div>
  );
}
